import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { addUpgradeReason, hasFeature } from '../../../../utils/general'
import { getPDFPageAnalytics } from '../../../../services/manage'
import { setShowPaymentModal } from '../../../../actions'
import { PLANS } from '../../../../constants/plans'
import { F_PDF_PAGE_ANALYTICS } from '../../../../constants/plans/constants'
import { transformAPIData } from '../utils/dataTransform'

const INITIAL_STATE = {
  totalViews: '0',
  uniqueVisitors: '0',
  avgSessionTime: 0,
  totalPages: 0
}

export const useAnalyticsPdf = ({ domain, period = '30d', selectedSource = 'all' }) => {
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const hasAccess = useMemo(() => hasFeature(productId, F_PDF_PAGE_ANALYTICS), [productId])

  const dispatch = useDispatch()
  const [pdfAnalytics, setPdfAnalytics] = useState([])
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false)
  const [overviewStats, setOverviewStats] = useState(INITIAL_STATE)
  const [pageTimeData, setPageTimeData] = useState([])
  const [sources, setSources] = useState([])
  const [linkAnalytics, setLinkAnalytics] = useState([])
  const [rawApiData, setRawApiData] = useState(null)
  const [error, setError] = useState(null)

  const cleanDomain = useMemo(() => {
    const domainValue = typeof domain === 'object' ? domain.subdomain : domain
    return domainValue?.replace(/^https?:\/\//, '')
  }, [domain])

  const handleUpgrade = () => {
    addUpgradeReason('View PDF Analytics')
    dispatch(
      setShowPaymentModal({
        showPaymentModal: true,
        upgradePlanId: PLANS.PRO.id
      })
    )
  }

  const updateState = (transformedData) => {
    setPdfAnalytics(transformedData.pdfAnalytics)
    setOverviewStats(transformedData.overviewStats)
    setPageTimeData(transformedData.pageTimeData)
    setSources(transformedData.sources)
    setLinkAnalytics(transformedData.linkAnalytics)
  }

  const resetState = () => {
    setPdfAnalytics([])
    setOverviewStats(INITIAL_STATE)
    setPageTimeData([])
    setSources([])
    setLinkAnalytics([])
    setRawApiData(null)
  }

  const fetchAnalytics = async (currentSelectedSource) => {
    if (!cleanDomain || !hasAccess) return

    setIsLoadingAnalytics(true)
    setError(null)

    try {
      const { data } = await getPDFPageAnalytics({
        domain: cleanDomain,
        period
      })

      setRawApiData(data)
      const transformedData = transformAPIData(data, currentSelectedSource || selectedSource)
      updateState(transformedData)
    } catch (error) {
      console.error('Error fetching PDF analytics:', error)
      setError(error.response?.data?.error || 'Failed to fetch analytics data')
      resetState()
    } finally {
      setIsLoadingAnalytics(false)
    }
  }

  useEffect(() => {
    if (rawApiData && selectedSource !== undefined) {
      const transformedData = transformAPIData(rawApiData, selectedSource)
      updateState(transformedData)
    }
  }, [selectedSource, rawApiData])

  useEffect(() => {
    fetchAnalytics(selectedSource)
  }, [cleanDomain, hasAccess, period])

  return {
    pdfAnalytics,
    isLoadingAnalytics,
    handleUpgrade,
    hasAccess,
    overviewStats,
    pageTimeData,
    sources,
    linkAnalytics,
    error
  }
}
